<template>
  <div class="filters-container" role="region" aria-label="Browse filters">
    <!-- CP-51: default shop chrome is chips. Dates, price, port, and ship
         stay behind progressive disclosure. CP-126: Sort is always visible in the toolbar. -->
    <div class="shop-row">
      <span class="chip-label" id="duration-label">Duration</span>
      <div class="chip-row" role="group" aria-labelledby="duration-label">
        <button
          v-for="opt in durationOptions"
          :key="opt.value || 'any'"
          type="button"
          class="filter-chip"
          :class="{ active: selectedDuration === opt.value }"
          :aria-pressed="selectedDuration === opt.value ? 'true' : 'false'"
          @click="selectDuration(opt.value)"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div class="shop-row">
      <span class="chip-label" id="destination-label">Destination</span>
      <div class="chip-row" role="group" aria-labelledby="destination-label">
        <button
          type="button"
          class="filter-chip"
          :class="{ active: !filters.destination }"
          :aria-pressed="!filters.destination ? 'true' : 'false'"
          @click="selectCategorical('destination', '')"
        >
          Any
        </button>
        <button
          v-for="dest in availableDestinations"
          :key="dest.id"
          type="button"
          class="filter-chip"
          :class="{ active: filters.destination === dest.id }"
          :aria-pressed="filters.destination === dest.id ? 'true' : 'false'"
          @click="selectCategorical('destination', dest.id)"
        >
          {{ dest.label }}
        </button>
      </div>
    </div>

    <div class="shop-row">
      <span class="chip-label" id="cruise-line-label">Cruise line</span>
      <div class="chip-row" role="group" aria-labelledby="cruise-line-label">
        <button
          type="button"
          class="filter-chip"
          :class="{ active: !filters.cruise_line }"
          :aria-pressed="!filters.cruise_line ? 'true' : 'false'"
          @click="selectCategorical('cruise_line', '')"
        >
          Any
        </button>
        <button
          v-for="line in availableCruiseLines"
          :key="line"
          type="button"
          class="filter-chip"
          :class="{ active: filters.cruise_line === line }"
          :aria-pressed="filters.cruise_line === line ? 'true' : 'false'"
          @click="selectCategorical('cruise_line', line)"
        >
          {{ line }}
        </button>
      </div>
    </div>

    <div v-if="activeChips.length" class="active-chips" aria-label="Active filters">
      <button
        v-for="chip in activeChips"
        :key="chip.key"
        type="button"
        class="active-chip"
        @click="clearChip(chip)"
      >
        <span>{{ chip.label }}</span>
        <i class="fas fa-times" aria-hidden="true"></i>
        <span class="sr-only">Remove {{ chip.label }}</span>
      </button>
    </div>

    <!-- CP-126: Sort always visible above the grid; dates/price/port/ship stay behind the toggle. -->
    <div class="filters-toolbar">
      <div class="toolbar-sort">
        <label for="sort-by" class="toolbar-sort-label">
          <i class="fas fa-sort-amount-down" aria-hidden="true"></i>
          Sort
        </label>
        <select id="sort-by" v-model="sortKey" class="toolbar-sort-select" @change="onSortChange">
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
          <option value="best_value-asc">Best value: low $/night first</option>
          <option value="best_value-desc">Best value: high $/night first</option>
          <option value="departure_date-asc">Departure date: earliest first</option>
          <option value="departure_date-desc">Departure date: latest first</option>
          <option value="duration-asc">Duration: short to long</option>
          <option value="duration-desc">Duration: long to short</option>
          <option value="name-asc">Name: A–Z</option>
          <option value="name-desc">Name: Z–A</option>
        </select>
      </div>

      <button
        type="button"
        class="filters-toggle"
        :aria-expanded="advancedOpen ? 'true' : 'false'"
        aria-controls="browse-advanced-filters"
        @click="togglePanel"
      >
        <span class="filters-toggle-label">
          <i class="fas fa-sliders-h" aria-hidden="true"></i>
          Dates, price &amp; more
          <span v-if="activeChipCount > 0" class="filters-count">{{ activeChipCount }}</span>
        </span>
        <i :class="advancedOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
      </button>
    </div>

    <div
      id="browse-advanced-filters"
      class="filters-panel"
      :class="{ 'is-open': advancedOpen }"
    >
      <div class="filters">
        <div class="filter-group">
          <SearchableSelect
            id="departure-port"
            label="Departure port"
            v-model="filters.departure_port"
            :options="departurePortSelectOptions"
            :disabled="isLoading"
            empty-label="All ports"
            placeholder="Search ports…"
            aria-label="Departure port"
          />
        </div>

        <div class="filter-group">
          <label for="ship">Ship</label>
          <select id="ship" v-model="filters.ship_name" :disabled="isLoading">
            <option value="">All ships</option>
            <option v-for="ship in availableShips" :key="ship" :value="ship">{{ ship }}</option>
          </select>
        </div>

        <!-- CP-28 ship-gate cleared 2026-09-18 (82.15% coverage). Departure
             Date Range re-enabled. -->
        <div class="filter-group">
          <label>Departure date range</label>
          <div class="date-range">
            <input
              type="date"
              v-model="filters.start_date"
              placeholder="Start date"
              class="date-input"
              :disabled="isLoading"
              aria-label="Departure date range start"
            />
            <span class="price-sep">to</span>
            <input
              type="date"
              v-model="filters.end_date"
              placeholder="End date"
              class="date-input"
              :disabled="isLoading"
              aria-label="Departure date range end"
            />
          </div>
        </div>

        <!-- CP-355: cabin class for /api/cruises?cabin=. Fares, the price range
             and the price sort below are that cabin's. Applied with the panel. -->
        <div class="filter-group">
          <label for="cabin">Cabin</label>
          <select id="cabin" v-model="filters.cabin" :disabled="isLoading" aria-describedby="cabin-hint">
            <option value="">Any cabin</option>
            <option v-for="cabin in cabinOptions" :key="cabin" :value="cabin">{{ cabin }}</option>
          </select>
          <span id="cabin-hint" class="filter-hint">Fares, price range and price sort use the cabin you pick.</span>
        </div>

        <div class="filter-group">
          <label>Price range ($)</label>
          <div class="price-range">
            <input
              type="number"
              v-model.number="filters.min_price"
              placeholder="Min"
              :min="filterOptions.priceRange.min"
              :max="filterOptions.priceRange.max"
              class="price-input"
              :disabled="isLoading"
              aria-label="Minimum price"
            />
            <span class="price-sep">to</span>
            <input
              type="number"
              v-model.number="filters.max_price"
              placeholder="Max"
              :min="filterOptions.priceRange.min"
              :max="filterOptions.priceRange.max"
              class="price-input"
              :disabled="isLoading"
              aria-label="Maximum price"
            />
          </div>
        </div>
      </div>

      <div class="filter-actions">
        <button type="button" @click="applyFilters" class="filter-button">Apply</button>
        <button type="button" @click="resetFilters" class="reset-button">Reset</button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { normalizeCanonicalDestinations } from '@/utils/canonicalDestinations';
import {
  normalizeDeparturePortOptions,
  labelForDeparturePort,
  resolveDeparturePortSelection
} from '@/utils/canonicalDeparturePorts';
import SearchableSelect from '@/components/SearchableSelect.vue';
import { facetOptionRequests, mergeFacetOptions } from '@/utils/facetOptions';
import {
  BROWSE_CABIN_VALUES,
  DEFAULT_BROWSE_SORT,
  apiFiltersFromQuery,
  browseQueryKey,
  browseStateFromQuery,
  emptyBrowseState,
  mergeBrowseQuery
} from '@/utils/browseQuery';

import { formatPriceAmount } from '@/utils/formatMoney';
import { appliedPanelChips, panelDraftReverts } from '@/utils/panelFilters';
// CP-128: fields hydrated from the URL into the local `filters` object.
const HYDRATED_FILTER_KEYS = [
  'destination',
  'cruise_line',
  'departure_port',
  'ship_name',
  'start_date',
  'end_date',
  'cabin',
  'min_price',
  'max_price',
  'include_past'
];
const CATEGORICAL_KEYS = ['destination', 'cruise_line', 'departure_port', 'ship_name'];

function blankFilters() {
  return {
    cruise_line: '',
    destination: '',
    departure_port: '',
    ship_name: '',
    start_date: '',
    end_date: '',
    cabin: '',
    min_price: null,
    max_price: null,
    include_past: false
  };
}

export default {
  name: 'CruiseFilter',
  components: { SearchableSelect },
  emits: ['filter-applied'],
  props: {
    cruiseLines: {
      type: Array,
      default: () => []
    },
    destinations: {
      type: Array,
      default: () => []
    },
    departurePorts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Collapsed on every viewport. Shop chips above are the default chrome.
      advancedOpen: false,
      filters: blankFilters(),
      // CP-121 hold: default stays Price low to high (backend default).
      sortKey: DEFAULT_BROWSE_SORT,
      selectedDuration: '',
      durationOptions: [
        { value: '', label: 'Any' },
        { value: '1-5', label: '1–5 nights' },
        { value: '6-9', label: '6–9 nights' },
        { value: '10-14', label: '10–14 nights' },
        { value: '15+', label: '15+ nights' }
      ],
      filterOptions: {
        cruiseLines: [],
        destinations: [],
        departurePorts: [],
        ships: [],
        durations: [],
        priceRange: { min: 0, max: 10000 }
      },
      isLoading: false,
      refetchFilterOptionsTimer: null,
      filterOptionsSeq: 0,
      // Set when URL hydration (load / back / forward) changed a categorical
      // filter: the next cascade pass refreshes options but does not clear
      // URL-provided values (the URL is the source of truth).
      skipSanitizeClearOnce: false
    };
  },
  created() {
    // CP-128: hydrate chips, Dates/price panel, and sort from the URL before
    // the first paint so Home search / deep links render as filtered.
    this.hydrateFromQuery(this.$route.query, null);
    this.fetchFilterOptions(this.filterParams());
  },
  computed: {
    availableCruiseLines() {
      const lines = this.filterOptions.cruiseLines.length > 0
        ? this.filterOptions.cruiseLines
        : this.cruiseLines;
      return [...lines].sort((a, b) => String(a).localeCompare(String(b)));
    },
    availableDestinations() {
      // CP-34: only {id, label} chips. Drop legacy raw strings (Western/Eastern/Southern Caribbean).
      const fromApi = normalizeCanonicalDestinations(this.filterOptions.destinations);
      if (fromApi.length > 0) return fromApi;
      return normalizeCanonicalDestinations(this.destinations);
    },
    availableDeparturePorts() {
      // CP-80: collapse codes / near-dupes / junk into city-name options.
      const raw = this.filterOptions.departurePorts.length > 0
        ? this.filterOptions.departurePorts
        : this.departurePorts;
      return normalizeDeparturePortOptions(raw);
    },
    departurePortSelectOptions() {
      return this.availableDeparturePorts.map((p) => ({ value: p.key, label: p.label }));
    },
    cabinOptions() {
      return BROWSE_CABIN_VALUES;
    },
    availableShips() {
      return this.filterOptions.ships || [];
    },
    // CP-355: the URL is what the results were fetched with.
    appliedState() {
      return browseStateFromQuery(this.$route.query);
    },
    activeChips() {
      // Chips and the toggle badge describe APPLIED filters only: a pending
      // panel pick (not yet Apply'd) must not look like it already filtered.
      return appliedPanelChips(this.appliedState, {
        port: (key) => labelForDeparturePort(this.availableDeparturePorts, key),
        price: formatPriceAmount
      });
      // CP-165: past sailings are never shown; no "Include past" chip.
    },
    activeChipCount() {
      return this.activeChips.length;
    }
  },
  watch: {
    'filters.cruise_line'() { this.refetchFilterOptionsAndSanitize('cruise_line'); },
    'filters.destination'() { this.refetchFilterOptionsAndSanitize('destination'); },
    'filters.departure_port'() { this.refetchFilterOptionsAndSanitize('departure_port'); },
    'filters.ship_name'() { this.refetchFilterOptionsAndSanitize('ship_name'); },
    '$route.query'(query, prevQuery) {
      // Back/forward, a shared link opened in-app, or our own URL write.
      // Our own writes already match local state, so only real changes land.
      if (this.$route.name !== 'CruiseList') return;
      this.hydrateFromQuery(query, prevQuery);
    }
  },
  methods: {
    /**
     * CP-128: copy URL state into the chips, panel inputs, and sort select.
     * With a previous query, only keys whose URL value changed are touched,
     * so an un-applied Dates/price draft survives an unrelated chip tap.
     */
    hydrateFromQuery(query, prevQuery) {
      const next = browseStateFromQuery(query);
      const prev = prevQuery ? browseStateFromQuery(prevQuery) : null;
      let categoricalChanged = false;
      HYDRATED_FILTER_KEYS.forEach((key) => {
        if (prev && prev[key] === next[key]) return;
        if (this.filters[key] === next[key]) return;
        this.filters[key] = next[key];
        if (CATEGORICAL_KEYS.includes(key)) categoricalChanged = true;
      });
      if (!prev || prev.duration !== next.duration) this.selectedDuration = next.duration;
      if (!prev || prev.sort !== next.sort) this.sortKey = next.sort;
      if (categoricalChanged) this.skipSanitizeClearOnce = true;
    },
    /**
     * CP-355: open/close the panel. Closing without Apply drops the draft back
     * to the applied values, so a hidden pending pick cannot be applied later
     * by accident.
     */
    togglePanel() {
      if (this.advancedOpen) this.revertPanelDraft();
      this.advancedOpen = !this.advancedOpen;
    },
    revertPanelDraft() {
      const reverts = panelDraftReverts(this.filters, this.appliedState);
      Object.keys(reverts).forEach((key) => {
        // The draft holds the canonical port key; the URL may carry a raw
        // value for the same port. Same label = nothing to revert.
        if (key === 'departure_port' && labelForDeparturePort(this.availableDeparturePorts, this.filters[key])
          === labelForDeparturePort(this.availableDeparturePorts, reverts[key])) return;
        this.filters[key] = reverts[key];
        if (CATEGORICAL_KEYS.includes(key)) this.skipSanitizeClearOnce = true;
      });
    },
    /** Current local UI state in browseQuery shape. */
    localState() {
      return {
        destination: this.filters.destination,
        cruise_line: this.filters.cruise_line,
        departure_port: this.filters.departure_port,
        ship_name: this.filters.ship_name,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        cabin: this.filters.cabin,
        duration: this.selectedDuration,
        min_price: this.filters.min_price,
        max_price: this.filters.max_price,
        sort: this.sortKey,
        include_past: false
      };
    },
    /**
     * CP-128: merge a patch into the current URL query (sibling params are
     * kept), tell Browse to fetch, and write the URL. Chips/sort use
     * router.replace (replaceState); Apply/Reset use router.push (pushState).
     */
    commitToUrl(patch, mode = 'replace') {
      const current = this.$route.query || {};
      const query = mergeBrowseQuery(current, patch);
      this.$emit('filter-applied', apiFiltersFromQuery(query));
      if (browseQueryKey(query) === browseQueryKey(current)) return;
      const navigate = mode === 'push' ? this.$router.push : this.$router.replace;
      navigate.call(this.$router, { path: this.$route.path, query, hash: this.$route.hash })
        .catch(() => {});
    },
    selectDuration(value) {
      this.selectedDuration = this.selectedDuration === value ? '' : value;
      this.commitToUrl({ duration: this.selectedDuration }, 'replace');
    },
    selectCategorical(field, value) {
      this.filters[field] = (value === '' || this.filters[field] === value) ? '' : value;
      this.commitToUrl({ [field]: this.filters[field] }, 'replace');
    },
    onSortChange() {
      this.commitToUrl({ sort: this.sortKey }, 'replace');
    },
    clearChip(chip) {
      if (chip.field === 'duration') {
        this.selectedDuration = '';
        this.commitToUrl({ duration: '' }, 'replace');
        return;
      }
      if (chip.field === 'min_price' || chip.field === 'max_price') {
        this.filters[chip.field] = null;
      } else if (chip.field === 'include_past') {
        this.filters.include_past = false;
      } else {
        this.filters[chip.field] = '';
      }
      this.commitToUrl({ [chip.field]: this.filters[chip.field] }, 'replace');
    },
    filterParams() {
      const p = {};
      if (this.filters.cruise_line) p.cruise_line = this.filters.cruise_line;
      if (this.filters.destination) p.destination = this.filters.destination;
      if (this.filters.departure_port) p.departure_port = this.filters.departure_port;
      if (this.filters.ship_name) p.ship_name = this.filters.ship_name;
      return p;
    },
    refetchFilterOptionsAndSanitize(changedKey) {
      if (this.refetchFilterOptionsTimer) clearTimeout(this.refetchFilterOptionsTimer);
      this.refetchFilterOptionsTimer = setTimeout(() => {
        this.refetchFilterOptionsTimer = null;
        const keepUrlValues = this.skipSanitizeClearOnce;
        this.skipSanitizeClearOnce = false;
        this.fetchFilterOptions(this.filterParams()).then((current) => {
          if (!current || keepUrlValues) return;
          const lines = this.filterOptions.cruiseLines || [];
          const dest = this.filterOptions.destinations || [];
          const ports = this.filterOptions.departurePorts || [];
          const ships = this.filterOptions.ships || [];
          const cleared = {};
          if (changedKey !== 'cruise_line' && this.filters.cruise_line && !lines.includes(this.filters.cruise_line)) {
            this.filters.cruise_line = '';
            cleared.cruise_line = '';
          }
          const destIds = dest.map(d => (d && d.id) ? d.id : d);
          if (changedKey !== 'destination' && this.filters.destination && !destIds.includes(this.filters.destination)) {
            this.filters.destination = '';
            cleared.destination = '';
          }
          // CP-80: options are canonical {key,label,rawValues}; resolve legacy raw/code.
          const portOptions = normalizeDeparturePortOptions(ports);
          if (changedKey !== 'departure_port' && this.filters.departure_port) {
            const resolved = resolveDeparturePortSelection(portOptions, this.filters.departure_port);
            if (!resolved) {
              this.filters.departure_port = '';
              cleared.departure_port = '';
            } else if (resolved !== this.filters.departure_port) {
              this.filters.departure_port = resolved;
            }
          }
          if (changedKey !== 'ship_name' && this.filters.ship_name && !ships.includes(this.filters.ship_name)) {
            this.filters.ship_name = '';
            cleared.ship_name = '';
          }
          // Chip taps already applied. If cascading options dropped a dependent
          // value, merge that into the URL so the catalog query matches the chips.
          if (Object.keys(cleared).length && (changedKey === 'destination' || changedKey === 'cruise_line')) {
            this.commitToUrl(cleared, 'replace');
          }
        });
      }, 50);
    },
    fetchFilterOptions(params = {}) {
      // Latest request wins so a slow unfiltered response cannot overwrite
      // options for the hydrated filters. Resolves true when still current.
      const seq = ++this.filterOptionsSeq;
      this.isLoading = true;
      // CP-133: a selected Destination / Cruise line chip must not collapse
      // its own row to Any + that chip. Each selected chip facet lists its
      // options from a request without its own value (other filters kept).
      const facetRequests = facetOptionRequests(params);
      const facetFetches = facetRequests.map((req) =>
        api.getFilterOptions(req.params)
          .then((res) => ({ field: req.field, data: res && res.data }))
          .catch(() => null)
      );
      return Promise.all([api.getFilterOptions(params), ...facetFetches])
        .then(([response, ...facetResults]) => {
          if (seq !== this.filterOptionsSeq) return false;
          const data = mergeFacetOptions(response.data, facetResults);
          this.filterOptions.cruiseLines = data.cruise_lines || [];
          this.filterOptions.destinations = normalizeCanonicalDestinations(data.destinations);
          this.filterOptions.departurePorts = data.departure_ports || [];
          // CP-80: map URL/legacy raw or code onto the canonical option key.
          if (this.filters.departure_port) {
            const resolved = resolveDeparturePortSelection(
              normalizeDeparturePortOptions(this.filterOptions.departurePorts),
              this.filters.departure_port
            );
            if (resolved && resolved !== this.filters.departure_port) {
              this.filters.departure_port = resolved;
            }
          }
          this.filterOptions.ships = data.ships || [];
          this.filterOptions.durations = data.durations || [];
          if (data.price_range) {
            this.filterOptions.priceRange = {
              min: data.price_range.min || 0,
              max: data.price_range.max || 10000
            };
          }
          this.$nextTick(() => this.revealActiveChips());
          return true;
        })
        .catch(error => {
          console.error('Error fetching filter options:', error);
          return false;
        })
        .finally(() => {
          if (seq === this.filterOptionsSeq) this.isLoading = false;
        });
    },
    /**
     * CP-133: a deep-linked chip (e.g. ?destination=south_pacific) can sit
     * past the edge of its scrolling rail. Scroll that rail (never the page)
     * so the selected chip is in view; no-op when it already is.
     */
    revealActiveChips() {
      if (!this.$el || typeof this.$el.querySelectorAll !== 'function') return;
      this.$el.querySelectorAll('.chip-row').forEach((row) => {
        const chip = row.querySelector('.filter-chip.active');
        if (!chip) return;
        const start = chip.offsetLeft;
        const end = start + chip.offsetWidth;
        const viewStart = row.scrollLeft;
        const viewEnd = viewStart + row.clientWidth;
        if (start >= viewStart && end <= viewEnd) return;
        row.scrollLeft = Math.max(0, start - 8);
      });
    },
    applyFilters() {
      // Apply commits the whole panel (dates, price, port, ship, past) plus
      // the chips and sort, as a new history entry.
      this.commitToUrl(this.localState(), 'push');
    },
    resetFilters() {
      this.filters = blankFilters();
      this.selectedDuration = '';
      this.sortKey = DEFAULT_BROWSE_SORT;
      this.commitToUrl(emptyBrowseState(), 'push');
    },
    /**
     * CP-127: empty-results recovery "Clear all filters". Same pushState
     * path as Reset (one history entry, URL is the source of truth) but the
     * shopper's sort choice is kept; sort is not a filter.
     */
    clearAllFilters() {
      this.filters = blankFilters();
      this.selectedDuration = '';
      this.commitToUrl({ ...emptyBrowseState(), sort: this.sortKey }, 'push');
    },
    /**
     * CP-127: drop a single filter (browseQuery patch, e.g. { max_price: null })
     * through the same replaceState merge as removing a chip. Siblings and
     * sort stay; no reload.
     */
    relaxFilter(patch) {
      const p = patch && typeof patch === 'object' ? patch : {};
      Object.keys(p).forEach((key) => {
        if (key === 'duration') {
          this.selectedDuration = p[key] || '';
        } else if (key === 'include_past') {
          this.filters.include_past = false; // CP-165: ignored
        } else if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
          this.filters[key] = p[key];
        }
      });
      this.commitToUrl(p, 'replace');
    }
  }
};
</script>

<style scoped>
.filters-container {
  background: transparent;
  border: none;
  padding: 0;
  margin-bottom: var(--space-4);
  box-shadow: none;
}

.shop-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
  min-width: 0;
}

.chip-label {
  margin: 0;
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: var(--text-caption);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.chip-row {
  /* CP-133: offsetParent for revealActiveChips(). */
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  gap: var(--space-2);
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: var(--cs-card-border) transparent;
  padding-bottom: 4px;
}

.chip-row::-webkit-scrollbar {
  height: 6px;
}

.chip-row::-webkit-scrollbar-thumb {
  background: var(--cs-card-border);
  border-radius: var(--radius-pill);
}

.filters-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.toolbar-sort {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  flex: 1 1 220px;
  max-width: 100%;
}

.toolbar-sort-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex: 0 0 auto;
  margin: 0;
  color: var(--cs-signal-teal);
  font-weight: 600;
  font-size: var(--text-small);
  white-space: nowrap;
}

.toolbar-sort-select {
  flex: 1 1 auto;
  min-width: 0;
  min-height: var(--touch-min);
  padding: 0 var(--space-3);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-pill);
  background: var(--cs-card);
  color: var(--cs-ink);
  font-size: var(--text-small);
  font-weight: 500;
  cursor: pointer;
}

.toolbar-sort-select:focus {
  outline: none;
  border-color: var(--cs-signal-teal);
  box-shadow: 0 0 0 2px var(--cs-signal-mist);
}

.filters-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: auto;
  flex: 0 0 auto;
  max-width: 100%;
  min-height: var(--touch-min);
  padding: 0 var(--space-4);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-pill);
  background: var(--cs-card);
  color: var(--cs-signal-teal);
  font-weight: 600;
  font-size: var(--text-small);
  cursor: pointer;
}

.filters-toggle[aria-expanded="true"] {
  background: var(--cs-signal-mist);
  border-color: var(--cs-signal-teal);
}

.filters-toggle-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.filters-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: var(--radius-pill);
  background: var(--cs-fill);
  color: white;
  font-size: var(--text-caption);
}

.active-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.active-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 32px;
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(11, 91, 99, 0.2);
  background: var(--cs-signal-mist);
  color: var(--cs-signal-teal);
  font-size: var(--text-caption);
  font-weight: 600;
  cursor: pointer;
}

.active-chip:hover {
  background: #d5ebed;
}

.filters-panel {
  display: none;
}

.filters-panel.is-open {
  display: block;
  margin-top: var(--space-3);
  padding: var(--space-4);
  background: var(--cs-card);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-card);
}

.filters {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.filter-group {
  width: 100%;
  min-width: 0;
}

.filter-group label {
  display: block;
  margin-bottom: var(--space-2);
  font-weight: 600;
  color: var(--color-text-muted);
  font-size: var(--text-small);
}

.filter-group select,
.filter-group input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--text-body);
  background: var(--color-surface);
  color: var(--color-text);
  box-sizing: border-box;
}

.filter-group select:focus,
.filter-group input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px var(--color-primary-light);
}

.filter-chip {
  flex: 0 0 auto;
  white-space: nowrap;
  min-height: var(--touch-min);
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--cs-card-border);
  background: var(--cs-card);
  color: var(--cs-ink);
  font-size: var(--text-small);
  font-weight: 500;
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
}

.filter-chip.active {
  background: var(--cs-fill);
  border-color: var(--cs-signal-teal);
  color: white;
}

.filter-chip:focus-visible,
.filters-toggle:focus-visible,
.toolbar-sort-select:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
}

.price-range {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.date-range {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.date-input {
  flex: 1;
  min-width: 0;
}

.price-input {
  flex: 1;
  min-width: 0;
}

.price-sep {
  color: var(--cs-muted);
  font-size: var(--text-small);
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: var(--space-2);
  font-weight: 500 !important;
  color: var(--color-text) !important;
  cursor: pointer;
}

.checkbox-input {
  width: auto !important;
  accent-color: var(--color-primary);
}

.filter-hint {
  display: block;
  margin-top: var(--space-1);
  font-size: var(--text-caption);
  color: var(--color-text-muted);
}

.filter-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

.filter-button,
.reset-button {
  width: 100%;
  min-height: var(--touch-min);
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--transition-normal);
}

.filter-button {
  background-color: var(--cs-fill);
  color: white;
  border: none;
}

.filter-button:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.reset-button {
  background-color: transparent;
  color: var(--cs-signal-teal);
  border: 1.5px solid var(--cs-signal-teal);
}

.reset-button:hover:not(:disabled) {
  background-color: var(--cs-signal-mist);
}

.filter-button:focus-visible,
.reset-button:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (min-width: 641px) {
  .filters-toolbar {
    flex-wrap: nowrap;
    justify-content: space-between;
  }

  .toolbar-sort {
    flex: 0 1 320px;
  }

  .shop-row {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--space-3);
  }

  .shop-row .chip-label {
    flex: 0 0 6.5rem;
    padding-top: 10px;
  }

  /* One scrolling rail per group so the chip band does not stack above the cards. */
  .chip-row {
    flex: 1;
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .filter-chip {
    min-height: 36px;
  }

  .filters {
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--space-4);
  }

  .filter-group {
    flex: 1 1 200px;
    max-width: 280px;
  }

  .filter-group-checkbox {
    flex: 1 1 100%;
    max-width: none;
  }

  .filter-actions {
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
  }

  .filter-button,
  .reset-button {
    width: auto;
    min-width: 120px;
  }
}
</style>
